import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { reportsApi } from '../lib/reportsApi';
import type { RegisterGroup, RegisterReport, RegisterScope } from '../lib/reportsApi';
import { ApiError, storedFileSrc } from '../lib/api';
import { useTenant } from '../context/TenantContext';

/**
 * THE MONTHLY ATTENDANCE REGISTER — names down the side, days across the top.
 *
 * ⚠ THIS SCREEN EXISTS TO BE PRINTED. The CSV export already gives one row per person per day,
 * which is what a spreadsheet wants and useless as a document. Every school in the country keeps
 * the other shape: the sheet they sign, file, and hand to an inspector. So the print layout is the
 * deliverable here and the on-screen view is the preview of it — not the other way round.
 *
 * ⚠ AND "PDF" IS THE PRINT DIALOG. Asked for a PDF, the honest answer on this stack is Save as PDF
 * in the browser's own print dialog: it needs no server-side renderer, it carries the school's
 * letterhead, and it produces a real A4 landscape page. The screen says so out loud, because
 * somebody looking for a button marked PDF will not find one otherwise.
 */
export function RegisterPage() {
  const { branding } = useTenant();

  const today = new Date();
  const [scope, setScope] = useState<RegisterScope>('students');
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [groups, setGroups] = useState<RegisterGroup[] | null>(null);
  /**
   * ⚠ `groups ?? []` IS NOT ENOUGH, AND THAT IS WHY THIS PAGE CRASHED.
   *
   * `??` only substitutes for null and undefined. An endpoint answering `{}` gets past it
   * untouched, and `({}).map` throws — `(groups ?? []).map is not a function`, which is exactly
   * what the page sweep reported. An array is what this screen needs, so an array is what it checks.
   */
  const groupList = Array.isArray(groups) ? groups : [];
  const [groupId, setGroupId] = useState<string>('');
  const [report, setReport] = useState<RegisterReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ⚠ The group list is refetched per scope and the selection cleared with it. Leaving a class id
  // selected while switching to staff would send an academicClassId to a department query — the
  // server ignores it, which is worse than an error because the screen would look filtered.
  useEffect(() => {
    let alive = true;
    setGroups(null); setGroupId(''); setReport(null);
    reportsApi.registerGroups(scope)
      .then((g) => { if (alive) setGroups(g); })
      .catch(() => { if (alive) setGroups([]); });
    return () => { alive = false; };
  }, [scope]);

  const load = useCallback(() => {
    setBusy(true); setError(null);
    reportsApi.register(scope, year, month, groupId || undefined)
      .then(setReport)
      .catch((e) => {
        setReport(null);
        setError(e instanceof ApiError ? e.message : 'Could not open the register.');
      })
      .finally(() => setBusy(false));
  }, [scope, year, month, groupId]);

  const monthValue = `${year}-${String(month).padStart(2, '0')}`;
  const logo = branding?.logoUrl ? logoSrc(branding.logoUrl) : null;

  // A wide grid needs a stable column width or the browser reflows every cell as data arrives.
  const dayCols = report?.days.length ?? 31;

  const totals = useMemo(() => {
    if (!report || report.rows.length === 0) return null;
    return {
      people: report.rows.length,
      present: report.rows.reduce((n, r) => n + r.present, 0),
      absent: report.rows.reduce((n, r) => n + r.absent, 0),
    };
  }, [report]);

  return (
    <div className="reg">
      <header className="reg-controls">
        <div>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Reports</div>
          <h1 className="h-display" style={{ fontSize: 28 }}>Attendance register</h1>
          <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4, maxWidth: '70ch' }}>
            The month on one sheet — names down the side, a column for every day, totals at the
            right. <Link className="link" to="/reports">The spreadsheet exports</Link> are the same figures one row
            per person per day.
          </p>
        </div>

        <div className="reg-filters card">
          <div className="reg-seg">
            <button className={scope === 'students' ? 'is-on' : ''} onClick={() => setScope('students')}>Students</button>
            <button className={scope === 'staff' ? 'is-on' : ''} onClick={() => setScope('staff')}>Staff</button>
          </div>

          <label>
            <span>Month</span>
            <input type="month" className="input" value={monthValue}
              onChange={(e) => {
                const [y, m] = e.target.value.split('-').map(Number);
                if (y && m) { setYear(y); setMonth(m); }
              }} />
          </label>

          <label>
            <span>{scope === 'staff' ? 'Department' : 'Class'}</span>
            <select className="input" value={groupId} onChange={(e) => setGroupId(e.target.value)}
              disabled={groups === null}>
              {/* ⚠ "Everyone" is offered but is never the default. A register of six hundred
                  children is forty pages nobody asked for, and the client's own words were that
                  nobody wants the whole school. */}
              <option value="">{groups === null ? 'Loading…' : `Everyone (${groupList.length ? 'all' : 'none'})`}</option>
              {groupList.map((g) => (
                <option key={g.id} value={g.id}>{g.label} · {g.people}</option>
              ))}
            </select>
          </label>

          <button className="btn btn-primary" onClick={load} disabled={busy}>
            {busy ? 'Opening…' : 'Open the register'}
          </button>

          {report && (
            <button className="btn" onClick={() => window.print()}>Print / save as PDF</button>
          )}
        </div>

        {report && (
          <p className="reg-hint">
            To keep a copy, press <strong>Print</strong> and choose <strong>Save as PDF</strong> as
            the destination. It prints landscape on A4.
          </p>
        )}

        {error && <div className="card reg-error">{error}</div>}
      </header>

      {!report && !busy && !error && (
        <div className="card" style={{ padding: 22, color: 'var(--ink-3)' }}>
          Choose a month and press <strong>Open the register</strong>.
        </div>
      )}

      {report && (
        <div className="reg-sheet">
          <div className="reg-head">
            {logo && <img className="reg-crest" src={logo} alt="" />}
            <div>
              <h2>{report.title || branding?.name}</h2>
              <p>
                Attendance register — <strong>{report.scope}</strong> · {report.monthName}
              </p>
            </div>
            <div className="reg-summary">
              <span><strong>{report.daysTaken}</strong> of {report.workingDays} working days taken</span>
              {totals && <span><strong>{totals.people}</strong> on the register</span>}
              <span>Average <strong>{report.averagePercent}%</strong></span>
            </div>
          </div>

          {/* ⚠ The sheet admits when it is incomplete, on the printed page and not only on screen.
              A register with four days missing looks identical to a complete one once printed, and
              the person signing it is the one who needs to know. */}
          {report.note && <p className="reg-note">{report.note}</p>}

          <div className="reg-scroll">
            <table className="reg-table" style={{ ['--days' as string]: String(dayCols) }}>
              <thead>
                {/* ⚠ PRINT-ONLY, AND IT REPEATS ON EVERY PAGE. Page two of this register was
                    twenty-three names and a grid of ticks with nothing on it saying whose school,
                    which class or which month — and schools file these sheets separately, so a
                    page that cannot be attributed is a page that gets thrown away. It lives inside
                    <thead> because a repeating header group is the only thing a browser reprints
                    at the top of each page; it is hidden on screen, where .reg-head already says
                    all of this. */}
                <tr className="reg-runhead">
                  <th colSpan={dayCols + 8}>
                    {report.title} · {report.scope} · {report.monthName}
                  </th>
                </tr>
                <tr>
                  <th className="c-sl">#</th>
                  <th className="c-name">Name</th>
                  <th className="c-code">{scope === 'staff' ? 'Code' : 'Adm. no.'}</th>
                  {report.days.map((d) => (
                    <th key={d.day}
                      className={`c-day${d.isSunday ? ' is-sun' : ''}${d.isHoliday ? ' is-hol' : ''}${!d.wasTaken && !d.isSunday && !d.isHoliday ? ' is-untaken' : ''}`}
                      title={`${d.date}${d.holidayName ? ` — ${d.holidayName}` : d.isSunday ? ' — Sunday' : ''}${!d.wasTaken && !d.isSunday && !d.isHoliday ? ' — register not taken' : ''}`}>
                      {d.day}
                    </th>
                  ))}
                  <th className="c-tot">P</th>
                  <th className="c-tot">A</th>
                  <th className="c-tot">CL</th>
                  <th className="c-tot">½</th>
                  <th className="c-pct">%</th>
                </tr>
              </thead>
              <tbody>
                {report.rows.map((r) => (
                  <tr key={r.personId}>
                    <td className="c-sl">{r.serial}</td>
                    <td className="c-name">{r.name}</td>
                    <td className="c-code">{r.code}</td>
                    {r.cells.map((c, i) => (
                      <td key={i} className={`c-day m-${markClass(c)}`}>{c}</td>
                    ))}
                    <td className="c-tot">{r.present}</td>
                    <td className="c-tot">{r.absent}</td>
                    <td className="c-tot">{r.leave}</td>
                    <td className="c-tot">{r.halfDays || ''}</td>
                    <td className="c-pct">{r.marked === 0 ? '—' : `${r.percent}%`}</td>
                  </tr>
                ))}
                {report.rows.length === 0 && (
                  <tr><td colSpan={dayCols + 8} className="reg-empty">Nobody is on this register.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="reg-legend">
            {report.legend.map((l) => (
              <span key={l.letter}><strong>{l.letter}</strong> {l.means}</span>
            ))}
          </div>

          <div className="reg-sign">
            <span>Class teacher</span><span>Head of school</span><span>Date</span>
          </div>
        </div>
      )}
    </div>
  );
}

/** ⚠ Keyed off the letter the server sent, so there is no second copy of the mark meanings here. */
function markClass(letter: string): string {
  switch (letter) {
    case 'P': return 'p';
    case 'A': return 'a';
    case 'L': return 'l';
    case 'CL': return 'cl';
    case '½': return 'half';
    case 'H': return 'h';
    case '—': return 'none';
    default: return 'blank';
  }
}

/** ⚠ apiUrl() is only correct for a stored path — an absolute or data URL must be left alone.
    Same trap as the question paper's letterhead; prefixing one produces a 404 and a hidden crest. */
function logoSrc(url: string): string {
  return storedFileSrc(url)!;   // ⚠ one shared implementation — see lib/api.ts
}
